/**
 * Catalogue des visites guidées du shell V2.
 *
 * Une visite est une suite de scènes. Chaque scène désigne un élément RÉEL de
 * la page — par son rôle et les clés i18n de son nom accessible, par le
 * libellé visible d'un champ qui n'a pas de nom, ou par une ancre structurelle
 * `data-ak-v2-component` — et ses textes vivent sous
 * `navigationV2.tours.catalogue.<visite>.scenes.<scène>`.
 *
 * Règle absolue, vérifiée par les tests du catalogue : **le guide n'agit
 * jamais à la place de la personne**. Il éclaire, explique, et attend — aucune
 * scène ne clique, ne coche, n'écrit ni n'enregistre. D'où :
 * - un bouton d'enregistrement, de création ou d'envoi est seulement montré
 *   (`gesture: "point"`), et ne fait jamais avancer la visite ;
 * - jamais d'exemple dans un identifiant, un mot de passe ou une adresse.
 */

import { FULLCARE_NAVIGATION_NODES } from "../registry";
import { accessibleName } from "./resolveTourTarget";

const freezeList = (values = []) => Object.freeze([...values]);

export const GUIDED_TOUR_TOPICS = Object.freeze(["interface", "park", "collection", "welfare", "team", "account"]);

const scene = ({
  id,
  path = null,
  navigate = null,
  target = null,
  // Geste montré à la personne : « click » (à elle de cliquer), « type » (à
  // elle d'écrire), « point » (rien à faire, on regarde).
  gesture = null,
  advance = "next",
  optional = false,
  // Textes partagés par plusieurs visites (entrée par le menu) au lieu des
  // textes propres à la visite.
  copy = null,
  // Scène sans objet dans l'état courant de la page (section déjà dépliée) :
  // elle est franchie dès que la cible est trouvée.
  skipWhen = null,
  // Ce que le geste fait apparaître ailleurs (la page épinglée dans le menu) :
  // la bulle ne le recouvre pas. La cible de la scène suivante l'est d'office.
  reveals = null,
  // Une scène facultative absente de la page est passée vite ; une scène
  // attendue laisse à la page le temps de charger ses données.
  timeout = optional ? 2500 : 9000,
}) => Object.freeze({ id, path, navigate, target, gesture, advance, optional, copy, skipWhen, reveals, timeout });

/** Scène où la personne écrit elle-même : le guide montre le champ et attend. */
export const sceneHandsOver = (item) => item?.gesture === "type";

// `continueTo` : page proposée à la fin (« Continuer à explorer »), à côté de « Terminer ».
const tour = ({ id, topic, features = [], destinations = [], continueTo = null, scenes }) => Object.freeze({
  id,
  topic,
  features: freezeList(features),
  destinations: freezeList(destinations),
  continueTo,
  scenes: freezeList(scenes),
});

const anchor = (component, suffix = "") => Object.freeze({ selector: `[data-ak-v2-component="${component}"]${suffix}` });
const button = (...nameKeys) => Object.freeze({ role: "button", nameKeys: freezeList(nameKeys) });
const field = (...nameKeys) => Object.freeze({ role: "textbox", nameKeys: freezeList(nameKeys) });
const choice = (...nameKeys) => Object.freeze({ role: "combobox", nameKeys: freezeList(nameKeys) });
const toggle = (...nameKeys) => Object.freeze({ role: "checkbox", nameKeys: freezeList(nameKeys) });
const radio = (...nameKeys) => Object.freeze({ role: "radio", nameKeys: freezeList(nameKeys) });
// Entrée d'un menu « ••• » (ActionMenu), ouvert par l'étape précédente.
const menuitem = (...nameKeys) => Object.freeze({ role: "menuitem", nameKeys: freezeList(nameKeys) });
// Bouton « ••• » de l'en-tête de la page (règles 7 et 8 de CLAUDE.md).
const headerMoreActions = Object.freeze({ role: "button", nameKeys: freezeList(["v2Patterns.list.moreActions"]), within: Object.freeze({ selector: '[data-ak-v2-component="page-header-actions"]' }) });
// Bloc titré d'une page V2 (Surface) : une région nommée par son titre.
const region = (...nameKeys) => Object.freeze({ role: "region", nameKeys: freezeList(nameKeys) });
// Dans la fenêtre ouverte en dernier ; un « l'un ou l'autre » porte la portée sur chaque option.
const inDialog = (descriptor) => (descriptor.anyOf
  ? Object.freeze({ anyOf: freezeList(descriptor.anyOf.map(inDialog)) })
  : Object.freeze({ ...descriptor, within: Object.freeze({ role: "dialog" }) }));
// Champ d'un formulaire embarqué sans nom accessible : son libellé visible et le bloc qui l'entoure.
const labelled = (closest, ...textKeys) => Object.freeze({ textKeys: freezeList(textKeys), closest });
// Section d'une fiche V2 : panneau de contexte à gauche sur grand écran (au
// premier niveau, ou en rubrique d'un domaine déplié pour les fiches à deux
// niveaux), onglets au-dessus du contenu sur écran étroit — ou, pour une fiche
// qui a le « plan de la fiche », la barre de repère qui nomme la rubrique
// ouverte. Le premier présent et visible gagne.
const RECORD_BAR = '[data-ak-v2-component="entity-record-bar"]';
const section = (...nameKeys) => Object.freeze({
  anyOf: freezeList([
    Object.freeze({ selector: '[data-ak-v2-component="entity-panel-section"]', nameKeys: freezeList(nameKeys) }),
    Object.freeze({ selector: '[data-ak-v2-component="entity-panel-subsection"]', nameKeys: freezeList(nameKeys) }),
    Object.freeze({ role: "tab", nameKeys: freezeList(nameKeys) }),
    Object.freeze({ selector: RECORD_BAR, nameKeys: freezeList(nameKeys) }),
  ]),
});
// Rubrique déjà ouverte, rien à cliquer : sur écran étroit, la barre de repère la nomme
// (l'ouvrir déplierait le plan par-dessus la suite) ; sur grand écran, sa ligne porte le
// repère de la page courante — ouvrir un domaine de la fiche ouvre sa première rubrique.
export const sectionAlreadyOpen = (element) => element?.matches?.(RECORD_BAR) === true
  || element?.getAttribute?.("aria-current") === "page";
// Action d'une liste : rendue tantôt en bouton, tantôt en lien.
const control = (...nameKeys) => Object.freeze({ anyOf: freezeList([button(...nameKeys), Object.freeze({ role: "link", nameKeys: freezeList(nameKeys) })]) });
// Champ de choix : par son nom accessible, sinon par son libellé visible.
const choiceOf = (...nameKeys) => Object.freeze({ anyOf: freezeList([choice(...nameKeys), labelledChoice(...nameKeys)]) });

const go = (destinationId, href) => Object.freeze({ destinationId, href });

// Le suivi du bien-être a ses propres dictionnaires (`src/welfare-follow-up/locales`) :
// une clé préfixée y est cherchée au lieu d'i18next (voir `translateTourTarget`).
export const FOLLOW_UP_TEXT_PREFIX = "welfareFollowUp:";
const followUpKey = (key) => `${FOLLOW_UP_TEXT_PREFIX}${key}`;

const VISIBLE = (selector) => {
  if (typeof document === "undefined") return false;
  const element = document.querySelector(selector);
  return Boolean(element && (element.offsetParent || element.getClientRects().length));
};

// Le choix du thème dans le panneau des réglages (WorkspacePrivacyDrawer).
const THEME_OPTIONS = '[data-ak-v2-component="theme-options"]';

const NODE_BY_ID = new Map(FULLCARE_NAVIGATION_NODES.map((node) => [node.id, node]));
const inMenu = (descriptor) => Object.freeze({ ...descriptor, within: anchor("navigation-menu") });
// Lien de menu d'une destination, indépendant de la langue : son adresse V2.
const MENU_LINK = (node) => {
  const pattern = node.versions?.v2?.pattern || "";
  const path = pattern.replace(/\/:[A-Za-z]+\??/g, "");
  return path ? `[data-ak-v2-component="navigation-menu"] a[href^="${path}"]` : null;
};

/**
 * Toutes les visites partent du même endroit — la page où la personne est —
 * et montrent d'abord le chemin dans le menu : la rubrique, puis l'entrée.
 * Les deux scènes sont facultatives : menu replié ou barre en mode icônes, la
 * visite continue et la scène suivante ouvre la page elle-même.
 */
const fromMenu = (destinationId) => {
  const node = NODE_BY_ID.get(destinationId);
  const group = node ? NODE_BY_ID.get(node.parentId) : null;
  if (!node || !group) return [];
  const copy = (id) => Object.freeze({ key: `navigationV2.tours.shared.${id}`, valueKeys: Object.freeze({ section: group.labelKey, item: node.labelKey }) });
  const link = MENU_LINK(node);
  return [
    scene({
      id: "menuSection", copy: copy("menuSection"), optional: true, timeout: 1200,
      // Rubrique déjà dépliée — l'entrée est sous les yeux : rien à ouvrir.
      skipWhen: link ? () => VISIBLE(link) : (element) => element.getAttribute("aria-expanded") === "true",
      target: inMenu({ role: "button", nameKeys: freezeList([group.labelKey]) }), gesture: "click", advance: "click",
    }),
    scene({
      id: "menuItem", copy: copy("menuItem"), optional: true, timeout: 2500,
      // Par son libellé, ou par son adresse quand le libellé n'est pas rendu (barre en icônes).
      target: Object.freeze({ anyOf: freezeList([
        inMenu({ role: "link", nameKeys: freezeList([node.labelKey]) }),
        ...(link ? [Object.freeze({ selector: link })] : []),
      ]) }),
      gesture: "click", advance: "click",
    }),
  ];
};

/**
 * Fiche à deux niveaux (l'établissement) : sur grand écran, une rubrique vit dans un
 * domaine du panneau, replié tant qu'on ne l'ouvre pas — sans cette scène, la visite
 * cherche une rubrique invisible (relevé en prod le 23/09/2026). Ouvrir le domaine
 * ouvre sa première rubrique. La scène mène à la même adresse que la rubrique qui suit :
 * hors de la fiche, on y arrive domaine déplié et la scène est franchie.
 *
 * Sur écran étroit, la barre de repère remplace le panneau : elle nomme déjà la
 * rubrique attendue (`sectionKey`), rien à ouvrir ; elle en nomme une autre, la
 * personne ouvre le plan, où la rubrique est à portée — sans cela la scène était
 * franchie et la rubrique suivante restait introuvable (recette 12.1.0-51, 921 px).
 */
const sameLabel = (name, label) => Boolean(label) && (name === label || name.startsWith(`${label} `));
const openArea = ({ areaKey, sectionKey, path, navigate }) => scene({
  id: "area", path, navigate, optional: true, timeout: 6000,
  copy: Object.freeze({ key: "navigationV2.tours.shared.openArea", valueKeys: Object.freeze({ area: areaKey }) }),
  // Sur écran étroit, la barre de repère remplace le panneau : elle ouvre le plan.
  target: Object.freeze({ anyOf: freezeList([
    Object.freeze({ selector: '[data-ak-v2-component="entity-panel-section"]', nameKeys: freezeList([areaKey]) }),
    Object.freeze({ selector: RECORD_BAR }),
  ]) }),
  gesture: "click", advance: "click",
  // Domaine déjà déplié (ses rubriques sont sous les yeux), ou rubrique déjà ouverte sur écran étroit.
  skipWhen: (element, t) => (element.matches?.(RECORD_BAR) === true
    ? sameLabel(accessibleName(element), typeof t === "function" ? String(t(sectionKey) || "").replace(/\s+/g, " ").trim().toLocaleLowerCase() : "")
    : element.getAttribute("aria-expanded") === "true"),
});

// Ligne d'un formulaire V1 : le libellé et son contrôle vivent dans deux colonnes. Jamais
// `[class*='col-']` : le libellé porte lui-même sa classe de colonne et `closest` le
// renverrait seul, sans le contrôle.
const FORM_ROW = ".row, .form-group, fieldset";
// Pied d'un formulaire V2 en création : « Créer » depuis la règle des formulaires
// (26/10/2026) ; « Enregistrer » avant, et toujours en modification.
const FORM_CREATE_BUTTON = button("v2Patterns.form.create", "common.save");
// Contrôle d'un champ V1 désigné par son libellé visible (le libellé n'est pas
// relié au contrôle : on remonte à la ligne, puis on prend le contrôle attendu).
const labelledControl = (role, ...textKeys) => Object.freeze({
  textKeys: freezeList(textKeys), closest: FORM_ROW, then: Object.freeze({ role }),
});
const labelledChoice = (...textKeys) => labelledControl("combobox", ...textKeys);
// Champ V2 : son libellé et son contrôle vivent dans le même bloc `[data-ak-v2-field]`.
const V2_FIELD = "[data-ak-v2-field]";
const v2Choice = (...nameKeys) => Object.freeze({ anyOf: freezeList([
  choice(...nameKeys),
  Object.freeze({ textKeys: freezeList(nameKeys), closest: V2_FIELD, then: Object.freeze({ role: "combobox" }) }),
]) });
// Fenêtre V1 (reactstrap 9) : un `FormGroup` rend un `div.mb-3` où le libellé n'est pas relié au contrôle.
const MODAL_FIELD = ".mb-3, .form-group";
const modalChoice = (...textKeys) => Object.freeze({ textKeys: freezeList(textKeys), closest: MODAL_FIELD, then: Object.freeze({ role: "combobox" }) });
// Fenêtre rendue en V2 sous le shell et en Classique ailleurs (assistant de création de
// tâche) : le champ V2 d'abord, la ligne de formulaire V1 en repli.
const wizardChoice = (...keys) => Object.freeze({ anyOf: freezeList([...v2Choice(...keys).anyOf, modalChoice(...keys)]) });

// Fiche d'un groupe, éditeur d'enrichissement.
const GROUP_RECORD = /^\/v2\/groups\/[^/]+/;
const ENRICHMENT_EDITOR = /^\/v2\/enrichments\/editor/;
const CUSTOM_PARAMETERS = /^\/v2\/custom-parameters/;
const SECTOR_RECORD = /^\/v2\/sectors\/[^/]+/;
const LIVE_SECTORS = /^\/v2\/live-sectors/;
const TIMELINE = /^\/v2\/timeline/;
const ACTIVITIES = /^\/v2\/activities/;
const VETERINARY = /^\/v2\/veterinary/;
const VETERINARY_CLINICAL = /^\/v2\/veterinary\/clinical/;
const VETERINARY_PHARMACY = /^\/v2\/veterinary\/pharmacy/;
const TRAINING_EXERCISES = /^\/v2\/resources\/exercises/;
const TRAINING_ANALYSIS = /^\/v2\/analysis\/training/;
const CAMPAIGN_LIST = /^\/v2\/campaigns\/?$/;
const CAMPAIGN_NEW = /^\/v2\/campaigns\/new/;
const HELP_CENTER = /^\/v2\/assistance\/help/;
const RESEARCH_LIST = /^\/v2\/research-projects\/?$/;
const RESEARCH_PROJECT = /^\/v2\/research-projects\/project\/[^/]+/;
const RESEARCH_LIST_OR_VIEW = /^\/v2\/research-projects(\/(actions|species|calendar|ideas|archives))?\/?$/;
// Onglets internes de la pharmacie (Stock, Mouvements, Emplacements) : `SectionTabs` sous le
// shell V2. Le panneau vétérinaire a aussi une rubrique « Stock » : on vise l'onglet, pas elle.
const pharmacyView = (nameKey) => Object.freeze({ role: "tab", nameKeys: freezeList([nameKey]), within: anchor("section-tabs") });
const tabAlreadySelected = (element) => element?.getAttribute?.("aria-selected") === "true";

// Étape de l'assistant de campagne : chaque étape du rail se clique librement.
const campaignStep = (stepKey) => Object.freeze({ ...button(`campaign.creation.steps.${stepKey}`), within: anchor("workflow-stepper") });
// Carte d'une méthode de collecte (interrupteur) : déjà activée, rien à cliquer.
const methodCard = (method) => Object.freeze({ selector: `[data-method="${method}"]` });
const methodOn = (element) => element?.getAttribute?.("aria-checked") === "true";
// « Nouvelle campagne » depuis la règle des listes (23/10/2026) ; « Créer une campagne » avant.
const CAMPAIGN_CREATE_BUTTON = button("campaignsV2.actions.create", "campaigns.CREATE_CAMPAIGN");

/**
 * Début commun des visites « une campagne par méthode » : la liste, puis la
 * fenêtre du type de campagne (suivi, formation…), qui mène à l'assistant sans
 * rien enregistrer. Arrivée directe sur l'assistant : la fenêtre est passée.
 */
const campaignStart = () => [
  ...fromMenu("welfare.campaigns"),
  scene({ id: "list", path: CAMPAIGN_LIST, navigate: go("welfare.campaigns", "/v2/campaigns"),
    target: CAMPAIGN_CREATE_BUTTON, gesture: "click", advance: "click" }),
  // L'option entière (son titre et sa description), pas le seul bouton radio.
  scene({ id: "kind", target: inDialog(labelled("label", "campaignsV2.create.training")), gesture: "point", optional: true }),
  scene({ id: "continue", target: inDialog(button("campaignsV2.create.confirm")), gesture: "click", advance: "click", optional: true }),
];
const campaignMethod = (method) => scene({ id: "method", path: CAMPAIGN_NEW, navigate: go("welfare.campaign-create", "/v2/campaigns/new"),
  target: methodCard(method), gesture: "click", advance: "click", skipWhen: methodOn });
const campaignStepScene = (id, stepKey) => scene({ id, path: CAMPAIGN_NEW, navigate: go("welfare.campaign-create", "/v2/campaigns/new"),
  target: campaignStep(stepKey), gesture: "click", advance: "click" });
// Ligne d'un réglage de collecte (libellé et contrôle) : on la montre.
const collectRow = (id, key, extra = {}) => scene({ id, path: CAMPAIGN_NEW, target: labelled(FORM_ROW, key), gesture: "point", optional: true, ...extra });
// « Lancer la campagne » : seul ce bouton crée la campagne. Le guide le montre.
const campaignLaunch = () => scene({ id: "launch", path: CAMPAIGN_NEW, navigate: go("welfare.campaign-create", "/v2/campaigns/new"),
  target: button("campaign.summary.launch_campaign"), gesture: "point" });
const MULTI_PARAMETER = /^\/v2\/analysis\/multi-parameter/;

export const GUIDED_TOURS = Object.freeze([
  tour({
    id: "interfaceDiscover",
    topic: "interface",
    // L'interface découverte, la suite naturelle est « Bien démarrer » et ses thèmes.
    continueTo: go("assistance.getting-started", "/v2/getting-started"),
    scenes: [
      scene({ id: "welcome" }),
      scene({ id: "menu", target: anchor("navigation-menu"), gesture: "point", optional: true }),
      scene({ id: "pin", target: anchor("navigation-current-pin"), gesture: "click", optional: true, timeout: 1200, reveals: anchor("navigation-pinned") }),
      scene({ id: "search", target: anchor("global-navigation-search", " input"), gesture: "click", optional: true }),
      // Sous 1280 px, les outils personnels sont repliés dans « Outils globaux » :
      // on ouvre d'abord le menu, sinon les scènes suivantes ne trouvent rien.
      scene({
        id: "openTools", copy: Object.freeze({ key: "navigationV2.tours.shared.openTools" }), optional: true, timeout: 1200,
        target: anchor("global-header-actions", ' [data-ak-v2-component="pressable"]'),
        gesture: "click", advance: "click", skipWhen: () => VISIBLE('[data-ak-v2-component="user-tools"]'),
      }),
      scene({ id: "tools", target: anchor("user-tools"), gesture: "point", optional: true, timeout: 1200 }),
      scene({ id: "guide", target: anchor("getting-started-entry"), gesture: "point", optional: true, timeout: 1200 }),
      // La visite se termine par un vrai choix, celui du thème : la personne
      // ouvre les réglages, puis clique le thème qui lui plaît (retour du
      // 22/09/2026 : « rien n'est modifié » était faux, et la personnalisation
      // de l'interface mérite d'être mise en avant). Le panneau rend le reste
      // de la page inerte, d'où la boussole avant lui.
      scene({
        id: "privacy", target: button("navigationV2.header.settings"), gesture: "click", advance: "click", optional: true, timeout: 1200,
        skipWhen: () => VISIBLE(THEME_OPTIONS),
      }),
      scene({ id: "theme", target: anchor("theme-options"), gesture: "click", advance: "click", optional: true }),
    ],
  }),
  tour({
    id: "accountPassword",
    topic: "account",
    destinations: ["account.settings"],
    scenes: [
      ...fromMenu("account.settings"),
      scene({ id: "open", path: /^\/v2\/account/, navigate: go("account.settings", "/v2/account?section=profile"),
        target: section("accountV2.sections.password"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "current", path: /^\/v2\/account/, navigate: go("account.settings", "/v2/account?section=password"), target: field("accountV2.password.current"), gesture: "type" }),
      scene({ id: "next", path: /^\/v2\/account/, navigate: go("account.settings", "/v2/account?section=password"), target: field("accountV2.password.next"), gesture: "type" }),
      scene({ id: "confirm", path: /^\/v2\/account/, navigate: go("account.settings", "/v2/account?section=password"), target: field("accountV2.password.confirm"), gesture: "type" }),
      scene({ id: "save", path: /^\/v2\/account/, navigate: go("account.settings", "/v2/account?section=password"), target: button("accountV2.actions.save"), gesture: "point" }),
    ],
  }),
  tour({
    id: "sectorsCreate",
    topic: "park",
    features: ["workspace.sectors"],
    destinations: ["collection.sectors", "collection.sector"],
    scenes: [
      ...fromMenu("collection.sectors"),
      scene({ id: "list", path: /^\/v2\/sectors\/?$/, navigate: go("collection.sectors", "/v2/sectors"),
        target: button("sector.listView.newSector"), gesture: "click", advance: "click" }),
      scene({ id: "name", path: /^\/v2\/sectors\/new/, navigate: go("collection.sector", "/v2/sectors/new"),
        target: anchor("legacy-sector-section", " fieldset input.form-control:not([type=color])"), gesture: "type" }),
      scene({ id: "color", path: /^\/v2\/sectors\/new/, navigate: go("collection.sector", "/v2/sectors/new"), target: labelled(FORM_ROW, "sector.managementView.sectorColor"),
        gesture: "point", optional: true }),
      scene({ id: "save", path: /^\/v2\/sectors\/new/, navigate: go("collection.sector", "/v2/sectors/new"), target: button("sector.CREATE_SECTOR"), gesture: "point" }),
    ],
  }),
  tour({
    id: "enclosuresCreate",
    topic: "park",
    features: ["workspace.enclosures"],
    destinations: ["collection.enclosures", "collection.enclosure"],
    scenes: [
      ...fromMenu("collection.enclosures"),
      scene({ id: "list", path: /^\/v2\/enclosures\/?$/, navigate: go("collection.enclosures", "/v2/enclosures"),
        target: button("enclosuresV2.registry.create", "enclosureList.createEnclosure"), gesture: "click", advance: "click" }),
      scene({ id: "name", path: /^\/v2\/enclosures\/new/, navigate: go("collection.enclosure", "/v2/enclosures/new"), target: field("enclosure.ENCLOSURE_NAME_PLACEHOLDER"), gesture: "type" }),
      scene({ id: "sector", path: /^\/v2\/enclosures\/new/, navigate: go("collection.enclosure", "/v2/enclosures/new"), target: labelledControl("combobox", "enclosure.SECTOR_LINKED"), gesture: "click", optional: true }),
      scene({ id: "save", path: /^\/v2\/enclosures\/new/, navigate: go("collection.enclosure", "/v2/enclosures/new"), target: button("enclosure.CREATE_ENCLOSURE_BUTTON"), gesture: "point" }),
    ],
  }),
  tour({
    id: "zimsConnect",
    topic: "park",
    features: ["zims.import", "zims.syncIn"],
    destinations: ["account.establishment"],
    scenes: [
      ...fromMenu("account.establishment"),
      openArea({ areaKey: "zooDetails.v2.areas.integrations", sectionKey: "zooDetails.tabs.zims", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims") }),
      scene({ id: "open", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"),
        target: section("zooDetails.tabs.zims"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      // Les champs d'identifiants n'existent qu'une fois la synchronisation activée : la
      // visite attend ce geste au lieu de proposer « Suivant » vers des champs absents.
      // Activer ne fait que préparer le formulaire — rien n'est enregistré sans la personne.
      scene({ id: "enable", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"), target: toggle("zooDetails.zimsSync.enableSync"),
        gesture: "click", advance: "click", skipWhen: (element) => element.checked === true || element.getAttribute("aria-checked") === "true" }),
      scene({ id: "login", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"), target: field("zooDetails.zimsSync.login"), gesture: "type" }),
      scene({ id: "password", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"), target: field("zooDetails.zimsSync.password"), gesture: "type" }),
      scene({ id: "collection", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"), target: toggle("zooDetails.zimsSync.collection.title"), gesture: "click", optional: true }),
      scene({ id: "save", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=zims"), target: button("zooDetails.zimsSync.saveCredentials"), gesture: "point" }),
    ],
  }),
  tour({
    id: "individualsCreate",
    topic: "collection",
    features: ["workspace.animals"],
    destinations: ["collection.individuals", "collection.individual-create"],
    scenes: [
      ...fromMenu("collection.individuals"),
      scene({ id: "list", path: /^\/v2\/individuals\/?$/, navigate: go("collection.individuals", "/v2/individuals"),
        target: button("individualsV2.actions.create", "specieAnimals.createIndividual"), gesture: "click", advance: "click" }),
      scene({ id: "species", path: /^\/v2\/individuals\/new/, navigate: go("collection.individual-create", "/v2/individuals/new"), target: button("animal.form.select", "animal.form.change"), gesture: "click" }),
      scene({ id: "name", path: /^\/v2\/individuals\/new/, navigate: go("collection.individual-create", "/v2/individuals/new"), target: field("animal.form.name"), gesture: "type" }),
      scene({ id: "sex", path: /^\/v2\/individuals\/new/, navigate: go("collection.individual-create", "/v2/individuals/new"), target: labelledControl("combobox", "animal.form.sexe"), gesture: "click", optional: true }),
      scene({ id: "enclosure", path: /^\/v2\/individuals\/new/, navigate: go("collection.individual-create", "/v2/individuals/new"), target: button("animal.form.enclosurePlaceholder"), gesture: "click", optional: true }),
      scene({ id: "save", path: /^\/v2\/individuals\/new/, navigate: go("collection.individual-create", "/v2/individuals/new"), target: button("animal.button.create"), gesture: "point" }),
    ],
  }),
  tour({
    id: "individualsImportZims",
    topic: "collection",
    features: ["zims.import"],
    destinations: ["collection.individuals", "integration.zims"],
    scenes: [
      ...fromMenu("collection.individuals"),
      // Règle des listes (23/10/2026) : les imports vivent dans « ••• » de l'en-tête (groupe Importer).
      scene({ id: "more", path: /^\/v2\/individuals\/?$/, navigate: go("collection.individuals", "/v2/individuals"),
        target: headerMoreActions, gesture: "click", advance: "click" }),
      scene({ id: "list", path: /^\/v2\/individuals\/?$/, navigate: go("collection.individuals", "/v2/individuals"),
        target: menuitem("individualsV2.actions.importZims"), gesture: "click", advance: "click" }),
      // Depuis le 03/11/2026, l'action ouvre la Collection ZIMS sur la fenêtre d'import
      // du fichier (traité comme une synchro), à la place de l'ancien tunnel en six étapes.
      scene({ id: "page", path: /^\/v2\/integrations\/zims/, navigate: go("integration.zims", "/v2/integrations/zims?tab=import&overlay=file"),
        target: inDialog(Object.freeze({ selector: "[data-ak-zims-file-import]" })), gesture: "point" }),
    ],
  }),
  tour({
    id: "groupsCreate",
    topic: "collection",
    features: ["workspace.groups"],
    destinations: ["collection.groups"],
    scenes: [
      ...fromMenu("collection.groups"),
      scene({ id: "list", path: /^\/v2\/groups\/?$/, navigate: go("collection.groups", "/v2/groups"),
        target: button("groupsV2.registry.create", "groupList.createGroup"), gesture: "click", advance: "click" }),
      // Fenêtre V2 de création (depuis le 09/10/2026) : le groupe part d'un enclos ou
      // d'une espèce, dont les individus sont pré-cochés. La leçon visait encore les
      // champs de l'ancienne fenêtre V1 et restait bloquée sur « Nommez le groupe ».
      scene({ id: "source", target: inDialog(Object.freeze({ selector: '[data-ak-v2-component="segmented-control"]', nameKeys: freezeList(["groupsV2.create.sourceLabel"]) })), gesture: "click" }),
      scene({ id: "pick", target: inDialog(v2Choice("groupsV2.create.pickEnclosure", "groupsV2.create.pickSpecies")), gesture: "click" }),
      // La liste des individus n'apparaît qu'une fois l'enclos ou l'espèce choisi.
      scene({ id: "animals", target: inDialog(region("groupsV2.create.animalsTitle")), gesture: "point", optional: true }),
      scene({ id: "name", target: inDialog(field("fastEvaluation.modal.groupName")), gesture: "type" }),
      scene({ id: "save", target: inDialog(FORM_CREATE_BUTTON), gesture: "point" }),
    ],
  }),
  tour({
    id: "fastEvaluationGroupCreate",
    topic: "collection",
    features: ["workspace.groups", "assessments.rapid"],
    destinations: ["welfare.fast-evaluation"],
    scenes: [
      ...fromMenu("welfare.fast-evaluation"),
      scene({ id: "list", path: /^\/v2\/fast-evaluations\/?$/, navigate: go("welfare.fast-evaluation", "/v2/fast-evaluations"),
        target: button("fastEvaluationsV2.newGroup"), gesture: "click", advance: "click" }),
      scene({ id: "name", target: inDialog(field("fastEvaluationsV2.groupName")), gesture: "type" }),
      // Mode de composition : contrôle segmenté (règle 6 de CLAUDE.md), plus un interrupteur.
      scene({ id: "mode", target: inDialog(radio("fastEvaluationsV2.simpleMode", "fastEvaluationsV2.individualMode")), gesture: "click", optional: true }),
      scene({ id: "count", target: inDialog(field("fastEvaluationsV2.individualCount")), gesture: "type", optional: true }),
      scene({ id: "species", target: inDialog(field("fastEvaluationsV2.searchSpecies")), gesture: "type", optional: true }),
      scene({ id: "save", target: inDialog(button("fastEvaluationsV2.create")), gesture: "point" }),
    ],
  }),
  tour({
    id: "fastEvaluationStart",
    topic: "welfare",
    features: ["assessments.rapid"],
    destinations: ["welfare.fast-evaluation"],
    scenes: [
      ...fromMenu("welfare.fast-evaluation"),
      scene({ id: "list", path: /^\/v2\/fast-evaluations\/?$/, navigate: go("welfare.fast-evaluation", "/v2/fast-evaluations"),
        target: button("fastEvaluationsV2.evaluate"), gesture: "click", advance: "click" }),
      scene({ id: "grid", path: /^\/v2\/fast-evaluations\/evaluate/,         target: { anyOf: freezeList([anchor("fast-official-grid", " button"), anchor("fast-custom-grid", " button")]) }, gesture: "click", advance: "click" }),
      scene({ id: "progress", path: /^\/v2\/fast-evaluations\/evaluate/, target: anchor("fast-evaluation-progress"), gesture: "point" }),
      scene({ id: "question", path: /^\/v2\/fast-evaluations\/evaluate/, target: anchor("fast-question-card"), gesture: "click" }),
      scene({ id: "submit", path: /^\/v2\/fast-evaluations\/evaluate/, target: button("fastEvaluationsV2.submitEvaluation"), gesture: "point" }),
    ],
  }),
  tour({
    id: "fastEvaluationResults",
    topic: "welfare",
    features: ["assessments.rapid"],
    destinations: ["welfare.fast-evaluation", "welfare.fast-evaluation-results"],
    scenes: [
      ...fromMenu("welfare.fast-evaluation"),
      scene({ id: "list", path: /^\/v2\/fast-evaluations\/?$/, navigate: go("welfare.fast-evaluation", "/v2/fast-evaluations"),
        // La carte d'un groupe s'ouvre sur ses résultats par son nom, vrai lien (règle 8 de CLAUDE.md).
        target: Object.freeze({ selector: '[data-ak-v2-component="registry-row-link"]' }), gesture: "click", advance: "click" }),
      scene({ id: "page", path: /^\/v2\/fast-evaluations\/results/, target: { selector: "main h1" }, gesture: "point", optional: true }),
    ],
  }),
  tour({
    id: "usersInvite",
    topic: "team",
    features: ["workspace.users"],
    destinations: ["account.users"],
    scenes: [
      ...fromMenu("account.users"),
      scene({ id: "steps", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"),
        target: { role: "tab", nameKeys: freezeList(["userCreation.ACCESS"]) }, gesture: "point" }),
      scene({ id: "identity", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"), target: field("user.FIRSTNAME"), gesture: "type" }),
      scene({ id: "email", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"), target: field("user.EMAIL"), gesture: "type" }),
      scene({ id: "role", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"), target: choice("userCreation.ROLE_CHOICE"), gesture: "click" }),
      scene({ id: "next", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"), target: button("common.NEXT"), gesture: "click", advance: "click" }),
      scene({ id: "send", path: /^\/v2\/users/, navigate: go("account.users", "/v2/users/invite"), target: button("usersV2.actions.send"), gesture: "point", optional: true }),
    ],
  }),
  tour({
    // Gérer le compte d'un collègue : son niveau d'accès, ses secteurs, ses
    // droits et son mot de passe. Tout se fait depuis l'établissement.
    id: "usersManageAccount",
    topic: "team",
    features: ["workspace.users"],
    destinations: ["account.establishment", "account.settings"],
    scenes: [
      ...fromMenu("account.establishment"),
      openArea({ areaKey: "zooDetails.v2.areas.team", sectionKey: "zooDetails.tabs.users", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=users") }),
      scene({ id: "section", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=users"),
        target: section("zooDetails.tabs.users"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "search", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=users"),
        target: field("zooDetails.v2.users.searchLabel"), gesture: "type" }),
      scene({ id: "open", path: /^\/v2\/establishment/, navigate: go("account.establishment", "/v2/establishment?section=users"),
        target: control("zooDetails.v2.users.openProfile"), gesture: "click", advance: "click" }),
      // La fiche d'un collègue s'ouvre depuis l'annuaire : elle n'a pas
      // d'adresse que la visite puisse deviner.
      scene({ id: "role", path: /^\/v2\/account/, target: choiceOf("accountV2.profile.role"), gesture: "click" }),
      scene({ id: "sectors", path: /^\/v2\/account/, target: choiceOf("accountV2.profile.sectors"), gesture: "click", optional: true }),
      // Le mot de passe se règle depuis le profil, avant de passer aux droits :
      // la section « Droits » ne porte que les modules (relevé le 20/09/2026).
      scene({ id: "password", path: /^\/v2\/account/, target: button("accountV2.actions.resetPassword"), gesture: "point" }),
      scene({ id: "rights", path: /^\/v2\/account/, target: section("accountV2.sections.rights"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "save", path: /^\/v2\/account/, target: button("accountV2.actions.save"), gesture: "point" }),
    ],
  }),
  tour({
    id: "campaignsCreate",
    topic: "welfare",
    features: ["observations.campaigns"],
    destinations: ["welfare.campaigns", "welfare.campaign-create"],
    scenes: [
      ...fromMenu("welfare.campaigns"),
      scene({ id: "list", path: /^\/v2\/campaigns\/?$/, navigate: go("welfare.campaigns", "/v2/campaigns"),
        target: CAMPAIGN_CREATE_BUTTON, gesture: "click", advance: "click" }),
      scene({ id: "steps", path: /^\/v2\/campaigns\/new/, navigate: go("welfare.campaign-create", "/v2/campaigns/new"), target: anchor("workflow-stepper"), gesture: "point" }),
      scene({ id: "name", path: /^\/v2\/campaigns\/new/, navigate: go("welfare.campaign-create", "/v2/campaigns/new"), target: labelledControl("textbox", "campaign.creation.basicInfo.campaignName"), gesture: "type", optional: true }),
      scene({ id: "methods", path: /^\/v2\/campaigns\/new/, navigate: go("welfare.campaign-create", "/v2/campaigns/new"), target: { selector: "[data-method]" }, gesture: "click", optional: true }),
      scene({ id: "next", path: /^\/v2\/campaigns\/new/, navigate: go("welfare.campaign-create", "/v2/campaigns/new"), target: button("campaign.creation.navigation.next"), gesture: "click", advance: "click" }),
    ],
  }),
  tour({
    // Placer des individus dans un groupe, depuis sa fiche : « Ajouter un animal »
    // (section Individus), puis l'enregistrement — seul lui écrit les membres du
    // groupe ; le guide le montre, sans jamais le cliquer.
    id: "groupMembersAssign",
    topic: "collection",
    features: ["workspace.animals"],
    destinations: ["collection.groups", "collection.group"],
    scenes: [
      ...fromMenu("collection.groups"),
      // Le groupe est au choix de la personne (un groupe en composition individuelle) :
      // la visite éclaire la liste et avance quand elle en ouvre un.
      // Le tableau des groupes porte son propre nom depuis la règle des listes (23/10/2026).
      scene({ id: "open", path: /^\/v2\/groups\/?$/, navigate: go("collection.groups", "/v2/groups"), target: anchor("groups-table"), gesture: "click", advance: "click" }),
      scene({ id: "section", path: GROUP_RECORD, target: section("fastEvaluation.tabs.animals"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "add", path: GROUP_RECORD, target: button("fastEvaluation.individualManagement.addAnimal"), gesture: "click", advance: "click" }),
      scene({ id: "search", target: inDialog(field("fastEvaluation.individualManagement.searchPlaceholder")), gesture: "type", optional: true }),
      scene({ id: "pick", target: inDialog({ selector: ".modal-body .list-group" }), gesture: "click", optional: true }),
      // « Ajouter (n) » n'apparaît qu'une fois un animal choisi ; il ferme la fenêtre sans rien enregistrer.
      scene({ id: "confirm", target: inDialog(button("fastEvaluation.individualManagement.confirmAddAnimals")), gesture: "click", advance: "click" }),
      scene({ id: "save", path: GROUP_RECORD, target: button("fastEvaluation.edit.update"), gesture: "point" }),
    ],
  }),
  tour({
    // Suivi du bien-être (13/10/2026) : la liste des suivis ouvre le dossier d'une cible, et
    // « Créer un objectif » ouvre une fenêtre par étapes. Rien n'est créé avant « Créer
    // l'objectif », que le guide montre seulement. Les textes visés sont ceux du suivi.
    id: "objectivesCreate",
    topic: "welfare",
    features: ["welfare.goals"],
    destinations: ["operations.objectives"],
    scenes: [
      ...fromMenu("operations.objectives"),
      scene({ id: "followUp", path: /^\/v2\/objectives\/?$/, navigate: go("operations.objectives", "/v2/objectives"),
        // Règle des listes (23/10/2026) : la ligne s'ouvre par le nom, vrai lien (plus d'icône « Ouvrir le suivi de … »).
        target: Object.freeze({ ...anchor("registry-row-link"), within: anchor("registry-table") }), gesture: "click", advance: "click" }),
      scene({ id: "create", target: Object.freeze({ anyOf: freezeList([button(followUpKey("createObjectiveCta")), button(followUpKey("newObjective"))]) }),
        gesture: "click", advance: "click" }),
      scene({ id: "title", target: inDialog(field(followUpKey("titleField"))), gesture: "type" }),
      scene({ id: "criterion", target: inDialog(field(followUpKey("criterion"))), gesture: "type" }),
      scene({ id: "next", target: inDialog(button(followUpKey("wizardNext"))), gesture: "click", advance: "click" }),
      scene({ id: "sources", target: inDialog(button(followUpKey("wizardNext"))), gesture: "click", advance: "click" }),
      scene({ id: "owner", target: inDialog(v2Choice(followUpKey("owner"))), gesture: "click" }),
      scene({ id: "checkDate", target: inDialog(labelled(V2_FIELD, followUpKey("checkDate"))), gesture: "point" }),
      scene({ id: "toActions", target: inDialog(button(followUpKey("wizardNext"))), gesture: "click", advance: "click" }),
      scene({ id: "save", target: inDialog(button(followUpKey("createObjective"))), gesture: "point" }),
    ],
  }),
  tour({
    // Assistant de création de tâche (trois étapes, rendu V2 sous le shell) : rien
    // n'est créé avant « Créer la tâche », que le guide montre seulement.
    id: "tasksCreate",
    topic: "park",
    features: ["logbook.tasks"],
    destinations: ["operations.tasks"],
    scenes: [
      ...fromMenu("operations.tasks"),
      scene({ id: "create", path: /^\/v2\/tasks\/?$/, navigate: go("operations.tasks", "/v2/tasks"), target: button("task.newTask"), gesture: "click", advance: "click" }),
      scene({ id: "title", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(field("task.creation.form.title")), gesture: "type" }),
      scene({ id: "category", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(wizardChoice("task.creation.form.mainCategory")), gesture: "click" }),
      scene({ id: "next", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(button("task.creation.buttons.next")), gesture: "click", advance: "click" }),
      scene({ id: "sector", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(wizardChoice("task.creation.form.sector")), gesture: "click" }),
      scene({ id: "responsibles", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(wizardChoice("task.creation.form.responsibles")), gesture: "click", optional: true }),
      scene({ id: "planning", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(button("task.creation.buttons.next")), gesture: "click", advance: "click" }),
      scene({ id: "save", path: /^\/v2\/tasks/, navigate: go("operations.tasks", "/v2/tasks?mode=create"), target: inDialog(button("task.creation.buttons.create")), gesture: "point" }),
    ],
  }),
  tour({
    // Modèle de rapport journalier : son nom, puis un premier bloc (question libre
    // ou paramètre suivi). Deux enregistrements, tous deux laissés à la personne.
    id: "dailyReportsTemplate",
    topic: "park",
    features: ["logbook.templates"],
    destinations: ["operations.daily-reports"],
    scenes: [
      ...fromMenu("operations.daily-reports"),
      scene({ id: "section", path: /^\/v2\/daily-reports/, navigate: go("operations.daily-reports", "/v2/daily-reports?section=templates"),
        target: section("dailyReportsHub.tabs.templates"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "create", path: /^\/v2\/daily-reports/, navigate: go("operations.daily-reports", "/v2/daily-reports?section=templates"),
        target: button("dailyReportsHub.templates.create"), gesture: "click", advance: "click" }),
      scene({ id: "owner", target: inDialog(v2Choice("dailyReportsHub.templates.assignTo")), gesture: "click", optional: true }),
      scene({ id: "name", target: inDialog(field("reportConfig.templateNamePlaceholder")), gesture: "type" }),
      scene({ id: "addBlock", target: inDialog(button("reportConfig.addBlock")), gesture: "click", advance: "click" }),
      scene({ id: "question", target: inDialog(field("reportConfig.block.questionPlaceholder")), gesture: "type" }),
      scene({ id: "kind", target: inDialog(labelled(MODAL_FIELD, "reportConfig.block.kind.label")), gesture: "point", optional: true }),
      scene({ id: "blockSave", target: inDialog(button("common.save")), gesture: "point" }),
      scene({ id: "save", target: inDialog(button("dailyReportsHub.templates.saveChanges")), gesture: "point" }),
    ],
  }),
  tour({
    id: "enrichmentsCreate",
    topic: "welfare",
    features: ["monitoring.enrichment"],
    destinations: ["enrichment.list", "enrichment.editor"],
    scenes: [
      ...fromMenu("enrichment.list"),
      scene({ id: "create", path: /^\/v2\/enrichments\/?$/, navigate: go("enrichment.list", "/v2/enrichments"), target: button("enrichmentsV2.create"), gesture: "click", advance: "click" }),
      scene({ id: "name", path: ENRICHMENT_EDITOR, navigate: go("enrichment.editor", "/v2/enrichments/editor"), target: field("enrichmentsV2.fields.name"), gesture: "type" }),
      scene({ id: "photo", path: ENRICHMENT_EDITOR, navigate: go("enrichment.editor", "/v2/enrichments/editor"), target: anchor("media-upload-queue"), gesture: "point", optional: true }),
      scene({ id: "sectors", path: ENRICHMENT_EDITOR, navigate: go("enrichment.editor", "/v2/enrichments/editor"), target: v2Choice("enrichmentsV2.filters.sectors"), gesture: "click" }),
      scene({ id: "stock", path: ENRICHMENT_EDITOR, navigate: go("enrichment.editor", "/v2/enrichments/editor"), target: toggle("enrichmentsV2.fields.stock"), gesture: "click", optional: true }),
      scene({ id: "save", path: ENRICHMENT_EDITOR, navigate: go("enrichment.editor", "/v2/enrichments/editor"),
        // Barre d'enregistrement commune (FormSaveBar) : visible dès que le nom est saisi.
        target: Object.freeze({ ...button("v2Patterns.form.create"), within: anchor("form-save-bar") }), gesture: "point" }),
    ],
  }),

  // ——— Les leçons pas à pas des tutos vidéo « Aller plus loin » ———
  // Chacune refait dans l'interface les gestes que montre sa vidéo (dépôt
  // akongo-motion-design, projets tuto-*), dans le même ordre.

  tour({
    // Tuto « Le cahier de secteur » : les notes qui remontent au secteur, ses
    // informations et ses documents, son rapport journalier et ses trois modes.
    // La fiche d'un secteur n'a pas d'adresse que la visite puisse deviner : la
    // personne en ouvre une depuis la liste.
    id: "sectorLogbookDiscover",
    topic: "park",
    features: ["logbook.entries"],
    destinations: ["collection.sectors", "collection.sector"],
    scenes: [
      ...fromMenu("collection.sectors"),
      scene({ id: "open", path: /^\/v2\/sectors\/?$/, navigate: go("collection.sectors", "/v2/sectors"), target: anchor("sector-card"), gesture: "click", advance: "click" }),
      scene({ id: "addNote", path: SECTOR_RECORD, target: button("sectorV2.actions.addNote"), gesture: "point" }),
      scene({ id: "notes", path: SECTOR_RECORD, target: section("sectorV2.navigation.sections.notes"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "tools", path: SECTOR_RECORD, target: section("sectorV2.navigation.sections.tools"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "documents", path: SECTOR_RECORD, target: section("sectorV2.navigation.subsections.documents"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      // La rubrique « Rapport » n'existe que si le rapport journalier du secteur est activé.
      scene({ id: "report", path: SECTOR_RECORD, target: section("sectorV2.navigation.sections.report"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      scene({ id: "config", path: SECTOR_RECORD, target: section("sectorV2.navigation.sections.main"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      scene({ id: "reportConfig", path: SECTOR_RECORD, target: section("sectorV2.navigation.subsections.report"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      // Les trois modes s'enregistrent d'eux-mêmes au changement : on les montre seulement.
      scene({ id: "modes", path: SECTOR_RECORD, target: labelled(".mb-4", "reportConfig.operatingModeLabel"), gesture: "point", optional: true }),
    ],
  }),
  tour({
    // Tuto « Le suivi quotidien » : la Vue Live (et la veille), la chronologie et
    // ses filtres, puis Mes rappels. Les règles d'alerte, réservées aux
    // administrateurs, sont seulement nommées.
    id: "dailyMonitoringDiscover",
    topic: "park",
    features: ["logbook.entries"],
    destinations: ["operations.live-sector", "operations.timeline", "personal.activities"],
    scenes: [
      ...fromMenu("operations.live-sector"),
      scene({ id: "live", path: LIVE_SECTORS, navigate: go("operations.live-sector", "/v2/live-sectors"), target: anchor("live-sector-summary"), gesture: "point" }),
      scene({ id: "sector", path: LIVE_SECTORS, navigate: go("operations.live-sector", "/v2/live-sectors"), target: anchor("live-sector-metrics"), gesture: "point", optional: true }),
      // Reculer d'un jour ne fait que changer la date affichée. La personne regarde la
      // veille avant de passer à la suite : la visite n'avance pas au clic.
      scene({ id: "previousDay", path: LIVE_SECTORS, navigate: go("operations.live-sector", "/v2/live-sectors"),
        target: button("liveSectorV2.actions.previousDay"), gesture: "click" }),
      scene({ id: "timeline", path: TIMELINE, navigate: go("operations.timeline", "/v2/timeline"), target: region("timelineV2.filters.ariaLabel"), gesture: "point" }),
      // Le bouton prend le nombre de filtres actifs pour nom dès qu'un filtre est posé.
      scene({ id: "filters", path: TIMELINE, navigate: go("operations.timeline", "/v2/timeline"), target: button("timeline.filters.advanced"), gesture: "point", optional: true }),
      scene({ id: "calendar", path: TIMELINE, navigate: go("operations.timeline", "/v2/timeline"), target: radio("timeline.viewMode.calendar"), gesture: "click" }),
      scene({ id: "reminders", path: ACTIVITIES, navigate: go("personal.activities", "/v2/activities"), target: anchor("activity-quick-add"), gesture: "point" }),
      scene({ id: "reminder", path: ACTIVITIES, navigate: go("personal.activities", "/v2/activities"), target: field("activities.form.titleLabel"), gesture: "type" }),
      // La cloche du bandeau : « Me le rappeler » ou « Convertir en tâche d'équipe ».
      scene({ id: "bell", target: control("navigationV2.header.notesAndReminders"), gesture: "point", optional: true }),
    ],
  }),

  tour({
    // Tuto « Les paramètres » : le catalogue AKONGO est déjà là ; le parc y ajoute
    // ses propres mesures, choisit leur type, leurs seuils et qui les voit.
    id: "parametersCreate",
    topic: "welfare",
    features: ["monitoring.individualParameters", "monitoring.environmentalParameters"],
    destinations: ["parameters.custom"],
    scenes: [
      ...fromMenu("parameters.custom"),
      // La liste ne s'affiche qu'avec au moins un paramètre personnalisé.
      scene({ id: "catalogue", path: CUSTOM_PARAMETERS, navigate: go("parameters.custom", "/v2/custom-parameters"), target: anchor("custom-parameters-table"), gesture: "point", optional: true }),
      scene({ id: "create", path: CUSTOM_PARAMETERS, navigate: go("parameters.custom", "/v2/custom-parameters"),
        target: button("customParameters.hub.newParameter"), gesture: "click", advance: "click" }),
      // Choisir le domaine ferme cette fenêtre et ouvre le formulaire : rien n'est créé.
      scene({ id: "domain", target: inDialog(radio("customParameters.domain.health", "customParameters.domain.environmental")), gesture: "click", advance: "click" }),
      scene({ id: "name", target: inDialog(field("customParametersV2.editor.name")), gesture: "type" }),
      scene({ id: "type", target: inDialog(v2Choice("customParametersV2.editor.type")), gesture: "click" }),
      // Les seuils n'existent que pour une mesure numérique.
      scene({ id: "limits", target: inDialog(toggle("customParametersV2.editor.limits")), gesture: "click", optional: true }),
      scene({ id: "visibility", target: inDialog(v2Choice("customParameters.visibility.label")), gesture: "click" }),
      scene({ id: "save", target: inDialog(FORM_CREATE_BUTTON), gesture: "point" }),
    ],
  }),
  tour({
    // Tuto « L'analyse multi-paramètres » : une série (paramètre, entité,
    // granularité, agrégateur), puis les vues, les ordonnances, l'export et la
    // vue enregistrée. Ajouter une série ne fait que l'afficher : rien n'est enregistré.
    id: "multiParameterAnalysisExplore",
    topic: "welfare",
    features: ["monitoring.crossAnalysis"],
    destinations: ["analysis.multi-parameter"],
    scenes: [
      ...fromMenu("analysis.multi-parameter"),
      scene({ id: "add", path: MULTI_PARAMETER, navigate: go("analysis.multi-parameter", "/v2/analysis/multi-parameter"),
        target: button("multiParameterAnalysis.seriesBuilder.add"), gesture: "click", advance: "click" }),
      scene({ id: "parameter", target: inDialog(labelled(MODAL_FIELD, "multiParameterAnalysis.seriesEditor.parameter")), gesture: "click" }),
      // Les entités, puis la granularité, n'apparaissent qu'une fois le choix précédent fait.
      scene({ id: "scope", target: inDialog(labelled(MODAL_FIELD, "multiParameterAnalysis.seriesEditor.scope")), gesture: "click", optional: true }),
      scene({ id: "bucket", target: inDialog(labelled(MODAL_FIELD, "multiParameterAnalysis.seriesEditor.bucket")), gesture: "click", optional: true }),
      scene({ id: "addSeries", target: inDialog(button("multiParameterAnalysis.seriesEditor.save")), gesture: "click", optional: true }),
      scene({ id: "mode", path: MULTI_PARAMETER, navigate: go("analysis.multi-parameter", "/v2/analysis/multi-parameter"),
        target: v2Choice("multiParameterAnalysis.filters.mode"), gesture: "click" }),
      // Les ordonnances se superposent à la série temporelle, quand le parc suit les traitements.
      scene({ id: "prescriptions", path: MULTI_PARAMETER, navigate: go("analysis.multi-parameter", "/v2/analysis/multi-parameter"),
        target: toggle("multiParameterAnalysis.filters.showPrescriptions"), gesture: "click", optional: true }),
      scene({ id: "stats", path: MULTI_PARAMETER, target: region("multiParameterAnalysis.card.stats"), gesture: "point", optional: true }),
      scene({ id: "export", path: MULTI_PARAMETER, navigate: go("analysis.multi-parameter", "/v2/analysis/multi-parameter"),
        target: button("multiParameterAnalysis.export.csv"), gesture: "point" }),
      scene({ id: "save", path: MULTI_PARAMETER, navigate: go("analysis.multi-parameter", "/v2/analysis/multi-parameter"),
        target: button("multiParameterAnalysis.savedViews.saveAsNew"), gesture: "point" }),
    ],
  }),
  tour({
    // Tuto « Le vétérinaire » : l'accueil des animaux en soin, les notes de santé,
    // la pharmacie, puis une ordonnance individuelle en trois étapes. Rien n'est
    // prescrit avant « Valider l'ordonnance », que le guide montre seulement.
    // La pharmacie n'est traversée que si le parc l'a : elle n'est pas exigée. Son
    // lien avec les ordonnances dépend du réglage du parc : la scène « stock » le dit.
    id: "veterinaryPrescription",
    topic: "welfare",
    features: ["logbook.treatments"],
    destinations: ["health.veterinary-dashboard", "health.veterinary-clinical"],
    scenes: [
      ...fromMenu("health.veterinary-dashboard"),
      scene({ id: "care", path: VETERINARY, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=animalsCare"), target: anchor("production-veterinary-workspace"), gesture: "point" }),
      scene({ id: "notes", path: VETERINARY, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=healthNotes"),
        target: section("veterinaryV2.navigation.subviews.healthNotes"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "pharmacy", path: VETERINARY, target: section("veterinaryV2.navigation.areas.pharmacy"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      scene({ id: "stock", path: VETERINARY_PHARMACY, target: anchor("metric-strip"), gesture: "point", optional: true }),
      scene({ id: "treatments", path: VETERINARY_CLINICAL, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=treatments"),
        target: section("veterinaryV2.navigation.subviews.treatments"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "prescription", path: VETERINARY_CLINICAL, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=treatments"),
        target: button("veterinaryDashboard.v2Header.newIndividualPrescription"), gesture: "click", advance: "click" }),
      scene({ id: "animal", target: inDialog(choice("veterinaryDashboard.treatments.selectAnimals", "veterinaryDashboard.treatments.selectAnimal")), gesture: "click" }),
      // Ce bouton ouvre l'assistant d'ordonnance : il ne prescrit rien.
      scene({ id: "openWizard", target: inDialog(button("veterinaryDashboard.treatments.newIndividualPrescription")), gesture: "click", advance: "click" }),
      scene({ id: "next", target: inDialog(button("prescription.v2.createModal.next")), gesture: "click", advance: "click" }),
      scene({ id: "medication", target: inDialog(choice("prescription.v2.createModal.medicationName")), gesture: "click" }),
      // Ajouter un traitement ne fait que l'inscrire dans l'ordonnance en cours.
      scene({ id: "addTreatment", target: inDialog(button("prescription.v2.createModal.addThisTreatment")), gesture: "click" }),
      scene({ id: "summary", target: inDialog(button("prescription.v2.createModal.next")), gesture: "click", advance: "click" }),
      // Ordonnance issue de la pharmacie, médicament du catalogue : ce que les
      // tâches sortiront du stock (sinon le bloc n'existe pas, la scène passe).
      scene({ id: "pharmacyRecap", target: inDialog(anchor("prescription-pharmacy-recap")), gesture: "point", optional: true }),
      // Stupéfiant prescrit : numéro de l'ordonnance sécurisée, saisi par la personne.
      scene({ id: "securePrescription", target: inDialog(anchor("prescription-secure-number")), gesture: "type", optional: true }),
      scene({ id: "task", target: inDialog(toggle("prescription.v2.createModal.taskAutomation.title")), gesture: "click", optional: true }),
      scene({ id: "validate", target: inDialog(button("prescription.v2.createModal.validate")), gesture: "point" }),
    ],
  }),
  tour({
    // Leçon « Une ordonnance pour plusieurs animaux » : le même bouton que
    // l'ordonnance individuelle, plusieurs animaux choisis, une saisie unique et
    // une ordonnance individuelle par animal (pas une ordonnance de groupe).
    id: "veterinaryMultiAnimalPrescription",
    topic: "welfare",
    features: ["logbook.treatments"],
    destinations: ["health.veterinary-dashboard", "health.veterinary-clinical"],
    scenes: [
      ...fromMenu("health.veterinary-dashboard"),
      scene({ id: "treatments", path: VETERINARY_CLINICAL, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=treatments"),
        target: section("veterinaryV2.navigation.subviews.treatments"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "prescription", path: VETERINARY_CLINICAL, navigate: go("health.veterinary-clinical", "/v2/veterinary/clinical?view=treatments"),
        target: button("veterinaryDashboard.v2Header.newIndividualPrescription"), gesture: "click", advance: "click" }),
      scene({ id: "animals", target: inDialog(choice("veterinaryDashboard.treatments.selectAnimals")), gesture: "click" }),
      // Ce bouton n'apparaît qu'à partir de deux animaux ; il ouvre l'assistant sans rien prescrire.
      scene({ id: "openWizard", target: inDialog(button("veterinaryDashboard.treatments.newMultiAnimalPrescription")), gesture: "click", advance: "click" }),
      scene({ id: "weights", target: inDialog(region("prescription.v2.multiAnimal.weightsTitle")), gesture: "point" }),
      scene({ id: "next", target: inDialog(button("prescription.v2.createModal.next")), gesture: "click", advance: "click" }),
      scene({ id: "medication", target: inDialog(choice("prescription.v2.createModal.medicationName")), gesture: "click" }),
      // Ajouter un traitement ne fait que l'inscrire dans l'ordonnance en cours.
      scene({ id: "addTreatment", target: inDialog(button("prescription.v2.createModal.addThisTreatment")), gesture: "click" }),
      scene({ id: "summary", target: inDialog(button("prescription.v2.createModal.next")), gesture: "click", advance: "click" }),
      scene({ id: "animalsList", target: inDialog(region("prescription.v2.multiAnimal.animalsTitle")), gesture: "point", optional: true }),
      // Une série de tâches par animal : le bloc « Pharmacie » compte le total pour tous.
      scene({ id: "pharmacyRecap", target: inDialog(anchor("prescription-pharmacy-recap")), gesture: "point", optional: true }),
      scene({ id: "validate", target: inDialog(button("prescription.v2.createModal.validate")), gesture: "point" }),
    ],
  }),
  tour({
    // Tuto « Le training » : un exercice du référentiel, la fiche de séance que
    // le soigneur remplit sur le mobile, puis l'analyse des apprentissages.
    id: "trainingExerciseCreate",
    topic: "welfare",
    features: ["monitoring.training"],
    destinations: ["training.exercises", "training.dashboard"],
    scenes: [
      ...fromMenu("training.exercises"),
      // La liste ne s'affiche qu'avec au moins un exercice.
      scene({ id: "catalogue", path: TRAINING_EXERCISES, navigate: go("training.exercises", "/v2/resources/exercises"), target: anchor("training-exercises-table"), gesture: "point", optional: true }),
      scene({ id: "create", path: TRAINING_EXERCISES, navigate: go("training.exercises", "/v2/resources/exercises"),
        target: button("training.referential.newExercise"), gesture: "click", advance: "click" }),
      scene({ id: "name", target: inDialog(field("training.exercise.name")), gesture: "type" }),
      scene({ id: "category", target: inDialog(choice("training.exercise.category")), gesture: "click" }),
      scene({ id: "objective", target: inDialog(field("training.exercise.objective")), gesture: "type" }),
      scene({ id: "species", target: inDialog(choice("training.exercise.targetSpecies")), gesture: "click", optional: true }),
      scene({ id: "save", target: inDialog(FORM_CREATE_BUTTON), gesture: "point" }),
      // Les onglets du référentiel vivent dans la page : aucune adresse ne les ouvre.
      scene({ id: "sheets", path: TRAINING_EXERCISES, navigate: go("training.exercises", "/v2/resources/exercises"),
        target: section("training.referential.tabSheets"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "newSheet", path: TRAINING_EXERCISES, target: button("training.sheet.newSheet"), gesture: "point", optional: true }),
      scene({ id: "analysis", path: TRAINING_ANALYSIS, navigate: go("training.dashboard", "/v2/analysis/training"), target: anchor("page-header"), gesture: "point" }),
    ],
  }),
  tour({
    // Tuto « Les scans » : une campagne en Scan Sampling — ses sessions, le mode
    // groupe, le plan de l'enclos et l'éthogramme. Rien n'est créé avant « Lancer
    // la campagne ».
    id: "scanCampaignCreate",
    topic: "welfare",
    features: ["observations.scan"],
    destinations: ["welfare.campaigns", "welfare.campaign-create"],
    scenes: [
      ...campaignStart(),
      campaignMethod("scan"),
      campaignStepScene("enclosures", "enclosureSelection"),
      campaignStepScene("collection", "collectionConfig"),
      collectRow("sessions", "campaign.creation.collectionConfig.scan.sessionDuration"),
      collectRow("groupMode", "campaign.creation.collectionConfig.scan.groupMode"),
      collectRow("location", "campaign.creation.collectionConfig.scan.enableLocation"),
      collectRow("behaviors", "campaign.creation.collectionConfig.scan.collectBehaviors"),
      campaignStepScene("summary", "summary"),
      campaignLaunch(),
    ],
  }),
  tour({
    // Tuto « Les focales » : une campagne en Focal Sampling — l'éthogramme, la
    // durée (une à soixante minutes) et le mode expert.
    id: "focalCampaignCreate",
    topic: "welfare",
    features: ["observations.focal"],
    destinations: ["welfare.campaigns", "welfare.campaign-create"],
    scenes: [
      ...campaignStart(),
      campaignMethod("focal"),
      campaignStepScene("collection", "collectionConfig"),
      collectRow("ethogram", "campaign.creation.collectionConfig.focal.observationProgram"),
      collectRow("duration", "campaign.creation.collectionConfig.focal.observationDuration"),
      collectRow("expertMode", "campaign.creation.collectionConfig.focal.expertMode"),
      campaignStepScene("summary", "summary"),
      campaignLaunch(),
    ],
  }),
  tour({
    // Tuto « Les questionnaires » : une campagne de questionnaire — les enclos,
    // le questionnaire et sa version, puis les alertes.
    id: "questionnaireCampaignCreate",
    topic: "welfare",
    features: ["assessments.species"],
    destinations: ["welfare.campaigns", "welfare.campaign-create"],
    scenes: [
      ...campaignStart(),
      campaignMethod("questionnaire"),
      campaignStepScene("enclosures", "enclosureSelection"),
      campaignStepScene("collection", "collectionConfig"),
      collectRow("questionnaire", "campaign.creation.collectionConfig.questionnaire.chooseQuestionnaire"),
      collectRow("version", "campaign.creation.collectionConfig.questionnaire.version"),
      campaignStepScene("alerts", "alertManagement"),
      campaignStepScene("summary", "summary"),
      campaignLaunch(),
    ],
  }),
  tour({
    // Tuto « Rafiki » : sa bulle vit dans les pages ; la Documentation l'offre à
    // tout le monde. La personne pose sa question elle-même.
    id: "rafikiAsk",
    topic: "interface",
    features: ["extensions.rafiki", "logbook.assistant"],
    destinations: ["assistance.help"],
    scenes: [
      ...fromMenu("assistance.help"),
      // Le bouton de la bulle n'a pas de nom accessible (une image) : sa classe le désigne.
      scene({ id: "bubble", path: HELP_CENTER, navigate: go("assistance.help", "/v2/assistance/help"),
        target: Object.freeze({ selector: "button.rafiki-bubble-btn" }), gesture: "click", advance: "click" }),
      scene({ id: "question", target: field("rafikiChat.inputPlaceholder"), gesture: "type" }),
      scene({ id: "sources", target: button("rafikiChat.buttons.viewContext"), gesture: "click", optional: true }),
      scene({ id: "fullscreen", target: button("rafikiChat.buttons.fullscreen"), gesture: "point", optional: true }),
      scene({ id: "check" }),
    ],
  }),
  tour({
    // Tuto « Les projets scientifiques », dans l'ordre de la vidéo : créer un projet
    // ou une idée (montrés seulement), ouvrir une fiche, ses rubriques (contributeurs,
    // demande, actions, documents, historique), l'export, puis les vues du quotidien
    // (par espèce, calendrier). Rien n'est créé ni exporté par le guide.
    id: "researchProjectsDiscover",
    topic: "welfare",
    features: ["extensions.researchProjects"],
    destinations: ["research.projects", "research.calendar"],
    scenes: [
      ...fromMenu("research.projects"),
      scene({ id: "overview", path: RESEARCH_LIST, navigate: go("research.projects", "/v2/research-projects"), target: region("researchProjectsV2.stats.ariaLabel"), gesture: "point", optional: true }),
      // Ces deux boutons ne s'affichent qu'à qui peut modifier les projets.
      scene({ id: "newProject", path: RESEARCH_LIST, navigate: go("research.projects", "/v2/research-projects"), target: button("researchProjectsV2.actions.newProject"), gesture: "point", optional: true }),
      scene({ id: "newIdea", path: RESEARCH_LIST, target: button("researchProjectsV2.actions.newIdea"), gesture: "point", optional: true }),
      // La personne ouvre un projet de la liste : la fiche n'a pas d'adresse que la visite connaisse.
      scene({ id: "open", path: RESEARCH_LIST, navigate: go("research.projects", "/v2/research-projects"), target: anchor("research-projects-table", " tbody tr"), gesture: "click", advance: "click" }),
      scene({ id: "contributors", path: RESEARCH_PROJECT, target: section("researchProjectsV2.detail.contributors"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "request", path: RESEARCH_PROJECT, target: section("researchProjectsV2.detail.request"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "actions", path: RESEARCH_PROJECT, target: section("researchProjectsV2.detail.actions"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      // « Ajouter » ouvre la fenêtre d'une action : le guide le montre seulement.
      scene({ id: "addAction", path: RESEARCH_PROJECT, target: button("researchProjectsV2.actions.add"), gesture: "point", optional: true }),
      scene({ id: "documents", path: RESEARCH_PROJECT, target: section("researchProjectsV2.sections.documents"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "history", path: RESEARCH_PROJECT, target: section("researchProjectsV2.sections.history"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      // « Exporter » ouvre la fenêtre des formats ; seul son bouton « Exporter » produit un fichier.
      scene({ id: "export", path: RESEARCH_PROJECT, target: button("researchProjectsV2.export.button"), gesture: "click", advance: "click" }),
      scene({ id: "formats", target: inDialog(Object.freeze({ selector: "[data-research-export]" })), gesture: "point" }),
      scene({ id: "exportSubmit", target: inDialog(button("researchProjectsV2.export.submit")), gesture: "point" }),
      // Retour aux vues du module : la fiche (et sa fenêtre) se ferme.
      scene({ id: "species", path: RESEARCH_LIST_OR_VIEW, navigate: go("research.projects", "/v2/research-projects"), target: section("researchProjectsV2.tabs.species"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "calendar", path: RESEARCH_LIST_OR_VIEW, navigate: go("research.calendar", "/v2/research-projects/calendar"), target: section("researchProjectsV2.tabs.calendar"), gesture: "click", advance: "click", skipWhen: sectionAlreadyOpen }),
      scene({ id: "reflex" }),
    ],
  }),
  tour({
    // Tuto « La pharmacie », dans l'ordre de la vidéo : le stock et ses alertes, les
    // emplacements, la réception (montrée), la fiche « Nouveau médicament » (ouverte puis
    // fermée sans rien enregistrer), les mouvements et les sorties non décomptées.
    // Rien n'est reçu, créé ni déplacé par le guide. Le lien ordonnances ↔ pharmacie
    // est un réglage du parc (`pharmacyConfig.prescriptionMode`, « off » par défaut) :
    // aucun texte ne le suppose actif.
    id: "pharmacyDiscover",
    topic: "welfare",
    features: ["logbook.pharmacy"],
    destinations: ["health.veterinary-dashboard", "health.veterinary-pharmacy"],
    scenes: [
      ...fromMenu("health.veterinary-dashboard"),
      scene({ id: "pharmacy", path: VETERINARY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: section("veterinaryV2.navigation.areas.pharmacy"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      // Les compteurs n'existent que dans l'onglet Stock (ouvert par défaut).
      scene({ id: "overview", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"), target: anchor("metric-strip"), gesture: "point", optional: true }),
      scene({ id: "locations", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.locations"), gesture: "click", advance: "click", skipWhen: tabAlreadySelected }),
      // « Nouvel emplacement » ouvre un formulaire : le guide le montre seulement.
      scene({ id: "addLocation", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"), target: button("pharmacy.locationsTab.add"), gesture: "point", optional: true }),
      scene({ id: "stock", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.stock"), gesture: "click", advance: "click", skipWhen: tabAlreadySelected }),
      // « Réception » ouvre son menu (Scan, Manuel) : le guide le montre, la personne choisit plus tard.
      scene({ id: "reception", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"), target: button("pharmacy.reception.open"), gesture: "point" }),
      scene({ id: "newMedication", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: button("pharmacy.newMedication"), gesture: "click", advance: "click" }),
      scene({ id: "steps", target: inDialog(anchor("workflow-stepper")), gesture: "point", optional: true }),
      scene({ id: "barcode", target: inDialog(labelled(MODAL_FIELD, "pharmacy.fields.barcodes")), gesture: "point" }),
      // La fiche se ferme sans rien enregistrer : « Annuler », geste de la personne.
      scene({ id: "close", target: inDialog(button("common.cancel")), gesture: "click", advance: "click" }),
      scene({ id: "movements", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.movements"), gesture: "click", advance: "click", skipWhen: tabAlreadySelected }),
      // Parc « Sans pharmacie » (défaut) : la carte rappelle le réglage et y mène.
      // Ordonnances liées à la pharmacie : seulement quand une tâche faite n'a pas
      // pu être décomptée. Les textes valent pour les deux cas.
      scene({ id: "uncounted", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="pharmacy-uncounted-outputs"]' }), gesture: "point", optional: true }),
      scene({ id: "reflex" }),
    ],
  }),
  tour({
    // Leçon complémentaire du tuto « Le vétérinaire » : les stupéfiants
    // (.scratch/pharmacie-stupefiants/, ADR 2026-11-04). L'emplacement réservé,
    // le marquage d'un médicament, ce que le registre change au stock, puis
    // l'onglet « Registre des stupéfiants » (s'il y a déjà un médicament marqué),
    // l'inventaire et les exports. Rien n'est créé, marqué, compté ni exporté par
    // le guide : les boutons sont montrés, la fiche « Nouvel emplacement » est
    // ouverte puis fermée par la personne.
    id: "pharmacyControlledDiscover",
    topic: "welfare",
    features: ["logbook.pharmacy"],
    destinations: ["health.veterinary-dashboard", "health.veterinary-pharmacy"],
    scenes: [
      ...fromMenu("health.veterinary-dashboard"),
      scene({ id: "pharmacy", path: VETERINARY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: section("veterinaryV2.navigation.areas.pharmacy"), gesture: "click", advance: "click", optional: true, skipWhen: sectionAlreadyOpen }),
      scene({ id: "locations", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.locations"), gesture: "click", advance: "click", skipWhen: tabAlreadySelected }),
      // Un emplacement déjà réservé porte son étiquette ; sinon la scène passe.
      scene({ id: "reservedBadge", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="location-controlled-badge"]' }), gesture: "point", optional: true }),
      scene({ id: "addLocation", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: button("pharmacy.locationsTab.add"), gesture: "click", advance: "click" }),
      scene({ id: "reserved", target: inDialog(toggle("pharmacy.locationsTab.controlledOnly")), gesture: "point" }),
      // La fiche se ferme sans rien enregistrer : « Annuler », geste de la personne.
      scene({ id: "closeLocation", target: inDialog(button("common.cancel")), gesture: "click", advance: "click" }),
      scene({ id: "stock", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.stock"), gesture: "click", advance: "click", skipWhen: tabAlreadySelected }),
      // La case « Stupéfiant (registre) » est à l'étape « Stock et conservation » de la fiche :
      // le guide montre le bouton et explique, sans ouvrir la fiche.
      scene({ id: "markMedication", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"), target: button("pharmacy.newMedication"), gesture: "point" }),
      scene({ id: "badge", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="pharmacy-medication-controlled-badge"]' }), gesture: "point", optional: true }),
      scene({ id: "misplaced", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="pharmacy-misplaced-stock"]' }), gesture: "point", optional: true }),
      // Ce que le registre change au stock : menu d'un lot (perte ou vol, destruction).
      scene({ id: "lotActions" }),
      // L'onglet n'existe qu'avec au moins un médicament marqué.
      scene({ id: "register", path: VETERINARY_PHARMACY, navigate: go("health.veterinary-pharmacy", "/v2/veterinary/pharmacy"),
        target: pharmacyView("pharmacy.views.register"), gesture: "click", advance: "click", optional: true, skipWhen: tabAlreadySelected }),
      scene({ id: "registerSummary", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="controlled-register-summary"]' }), gesture: "point", optional: true }),
      scene({ id: "registerTable", path: VETERINARY_PHARMACY, target: Object.freeze({ selector: '[data-testid="controlled-register-table"]' }), gesture: "point", optional: true }),
      scene({ id: "inventory", path: VETERINARY_PHARMACY, target: button("pharmacy.controlled.inventory.open"), gesture: "point", optional: true }),
      scene({ id: "export", path: VETERINARY_PHARMACY, target: button("pharmacy.controlled.export"), gesture: "point", optional: true }),
      scene({ id: "reflex" }),
    ],
  }),
]);

export const GUIDED_TOUR_BY_ID = Object.freeze(Object.fromEntries(GUIDED_TOURS.map((item) => [item.id, item])));

/**
 * Visites proposées à la personne : l'offre du parc doit inclure une des
 * capacités de la visite, et la personne doit pouvoir ouvrir ses pages.
 */
export function availableGuidedTours({ scope, canReach }) {
  return GUIDED_TOURS.filter((item) => (!item.features.length || item.features.some((id) => scope.can(id)))
    && item.destinations.every((id) => canReach(id)));
}
